import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { OrderCard } from "@/components/orders/OrderCard";
import { StatusGroupHeader } from "@/components/orders/StatusGroupHeader";
import { Plus, Search, Monitor, Map, Clock, ShoppingBag, ExternalLink, ChevronLeft, ChevronRight, History, Settings } from "lucide-react";
import { STATUS_CONFIG } from "@/components/orders/statusConfig";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function OrdersListPanel({
  loading,
  orders,
  filteredOrders,
  groupedOrders,
  groupByStatus,
  filter,
  searchQuery,
  stats,
  deliverers,
  selectedOrderId,
  newOrderAlertId,
  statusConfig,
  onFilterChange,
  onSearchChange,
  onSelectOrder,
  onAdvance,
  dismissAlert,
  isToday,
  historyOrders = [],
  historyLoading = false,
  historyError = false,
  historyTotal = 0,
  historyOffset = 0,
  historyLimit = 50,
  onHistoryPageChange,
}) {
  const navigate = useNavigate();
  const [showConfig, setShowConfig] = useState(false);
  const cancelledCount = orders.filter(o => o.status === "cancelled").length;
  const filterTabs = [
    { key: "todos", label: "TODOS", count: filteredOrders.length },
    { key: "hoy", label: "HOJE", count: orders.filter(o => isToday(o.created_at) && !o.hidden && o.status !== "cancelled").length },
    { key: "agendados", label: "AGENDADOS", count: orders.filter(o => o.scheduled_for && !o.hidden && !["delivered", "cancelled", "rejected"].includes(o.status)).length },
    { key: "cancelados", label: "CANCELADOS", count: cancelledCount, red: cancelledCount > 0 },
    { key: "ocultos", label: "OCULTOS", count: orders.filter(o => o.hidden).length },
    { key: "historico", label: "HISTÓRICO" },
  ];

  return (
    <div className={`w-full md:w-80 lg:w-[440px] border-r flex flex-col bg-white ${selectedOrderId ? "hidden md:flex" : "flex"}`} data-testid="orders-list-panel">
      {/* Filter Tabs + Actions */}
      <div className="border-b">
        <div className="flex items-center gap-1 px-3 pt-2.5 pb-1 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-emerald-600 font-bold text-xs h-8 px-2"
            onClick={() => navigate("/orders/new")}
            data-testid="new-order-btn"
          >
            <Plus className="w-3.5 h-3.5" />NOVO
          </Button>
          {filterTabs.map(f => (
            <button
              key={f.key}
              className={`px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide rounded transition-colors flex items-center gap-1 ${
                filter === f.key ? "text-emerald-700 bg-emerald-50" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => onFilterChange(f.key)}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
              {f.count > 0 && (
                <span className={`text-[9px] min-w-[16px] h-4 flex items-center justify-center rounded-full font-bold ${
                  filter === f.key
                    ? (f.red ? "bg-red-600 text-white" : "bg-emerald-600 text-white")
                    : (f.red ? "bg-red-100 text-red-700" : "bg-gray-200 text-gray-600")
                }`}>{f.count}</span>
              )}
            </button>
          ))}
          <div className="border-l border-gray-200 mx-1 h-5" />
          <button
            className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-blue-600 hover:bg-blue-50 rounded flex items-center gap-1"
            onClick={() => window.open("/kitchen", "_blank")}
            data-testid="filter-monitor"
          >
            <Monitor className="w-3 h-3" />MONITOR
            <ExternalLink className="w-2.5 h-2.5 opacity-50" />
          </button>
          <button
            className="px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-purple-600 hover:bg-purple-50 rounded flex items-center gap-1"
            onClick={() => window.open("/map", "_blank")}
            data-testid="filter-mapa"
          >
            <Map className="w-3 h-3" />MAPA
            <ExternalLink className="w-2.5 h-2.5 opacity-50" />
          </button>
          <button
            className="ml-auto px-2 py-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded"
            onClick={() => setShowConfig(true)}
            data-testid="config-btn"
            title="Configurações"
          >
            <Settings className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-3 px-3 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Buscar pedido, cliente, telefone..."
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              className="pl-9 bg-gray-50 border-gray-200 h-9 text-sm"
              data-testid="search-orders"
            />
          </div>
          <div className="flex items-center gap-1.5" data-testid="status-counters">
            {[
              { status: "pending", count: stats.pending, color: "bg-red-500" },
              { status: "preparing", count: stats.preparing, color: "bg-blue-500" },
              { status: "ready", count: stats.ready, color: "bg-emerald-500" },
              { status: "delivering", count: stats.delivering, color: "bg-purple-500" },
            ].filter(s => s.count > 0).map(s => (
              <span
                key={s.status}
                className={`${s.color} text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1`}
                title={STATUS_CONFIG[s.status]?.label}
                data-testid={`counter-${s.status}`}
              >{s.count}</span>
            ))}
            {stats.avgPrepTime > 0 && (
              <span className="text-[10px] text-gray-400 flex items-center gap-0.5 ml-1" data-testid="avg-prep-time">
                <Clock className="w-3 h-3" />{stats.avgPrepTime}m
              </span>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="flex-1">
        {filter === "historico" ? (
          <>
            {historyLoading && <LoadingScreen />}
            {!historyLoading && (
              <div className="divide-y divide-gray-100">
                {historyOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    deliverers={deliverers}
                    selectedOrderId={selectedOrderId}
                    newOrderAlertId={newOrderAlertId}
                    groupByStatus={false}
                    statusConfig={statusConfig}
                    onSelect={onSelectOrder}
                    onAdvance={onAdvance}
                    dismissAlert={dismissAlert}
                  />
                ))}
                {historyOrders.length === 0 && !historyError && (
                  <div className="p-8 text-center">
                    <History className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Sem histórico</p>
                  </div>
                )}
                {historyOrders.length === 0 && historyError && (
                  <div className="p-8 text-center">
                    <History className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-400">Não foi possível carregar o histórico.</p>
                    <p className="text-xs text-gray-300 mt-1">Tente novamente mais tarde.</p>
                  </div>
                )}
              </div>
            )}
            {historyTotal > historyLimit && (
              <div className="flex items-center justify-between px-4 py-3 border-t text-xs text-gray-500">
                <span>{historyOffset + 1}–{Math.min(historyOffset + historyLimit, historyTotal)} de {historyTotal}</span>
                <div className="flex gap-1">
                  <button
                    disabled={historyOffset === 0}
                    onClick={() => onHistoryPageChange(historyOffset - historyLimit)}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    disabled={historyOffset + historyLimit >= historyTotal}
                    onClick={() => onHistoryPageChange(historyOffset + historyLimit)}
                    className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {loading && <LoadingScreen />}
            {!loading && groupByStatus && groupedOrders ? (
              Object.entries(groupedOrders).map(([status, statusOrders]) => (
                <div key={status}>
                  <StatusGroupHeader
                    status={status}
                    count={statusOrders.length}
                    totalValue={statusOrders.reduce((s, o) => s + (o.total || 0), 0)}
                  />
                  <div className="divide-y divide-gray-100">
                    {statusOrders.map(order => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        deliverers={deliverers}
                        selectedOrderId={selectedOrderId}
                        newOrderAlertId={newOrderAlertId}
                        groupByStatus={groupByStatus}
                        statusConfig={statusConfig}
                        onSelect={onSelectOrder}
                        onAdvance={onAdvance}
                        dismissAlert={dismissAlert}
                      />
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredOrders.map(order => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    deliverers={deliverers}
                    selectedOrderId={selectedOrderId}
                    newOrderAlertId={newOrderAlertId}
                    groupByStatus={groupByStatus}
                    statusConfig={statusConfig}
                    onSelect={onSelectOrder}
                    onAdvance={onAdvance}
                    dismissAlert={dismissAlert}
                  />
                ))}
              </div>
            )}
            {filteredOrders.length === 0 && !loading && (
              <div className="p-8 text-center">
                <ShoppingBag className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">Sem pedidos</p>
              </div>
            )}
          </>
        )}
      </ScrollArea>

      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Configurações</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Modos de entrega visíveis</p>
              <div className="space-y-2">
                {[
                  { label: "Delivery" },
                  { label: "Retirada no local" },
                  { label: "Consumo na mesa" },
                  { label: "Balcão / POS" },
                ].map(mode => (
                  <label key={mode.label} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" defaultChecked className="rounded border-gray-300 accent-emerald-600" />
                    <span className="text-sm">{mode.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
